import React, { useEffect, useState } from "react";
import { Share2, Plus, CheckCircle2, Clock, AlertTriangle, RefreshCw, Loader2, Linkedin, Facebook, Instagram, Store, Trash2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { MetricoolComposer } from "./MetricoolComposer";
import RecurringPromos from "./RecurringPromos";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const NET_ICON = { linkedin: Linkedin, facebook: Facebook, instagram: Instagram, google: Store };
const NET_LABEL = { linkedin: "LinkedIn", facebook: "Facebook", instagram: "Instagram", google: "Google Business" };
const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const STATE = {
  ready: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  not_configured: { cls: "bg-slate-50 text-slate-600 border-slate-200", icon: Clock },
  auth_error: { cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
  error: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle },
};

export default function MetricoolManager() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadStatus = () => { setChecking(true); adminApi.metricool.status().then(setStatus).catch(() => setStatus(null)).finally(() => setChecking(false)); };
  const loadPosts = () => adminApi.metricool.posts().then(setPosts).catch(() => setPosts([]));
  useEffect(() => { loadStatus(); loadPosts(); }, []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await adminApi.metricool.remove(deleteId);
      toast({ title: res?.removed_in_metricool ? "Post deleted (removed from Metricool too)" : "Removed from the log", description: res?.removed_in_metricool ? undefined : "It may have already been removed or published in Metricool." });
      loadPosts();
    } catch {
      toast({ title: "Couldn't delete the post", variant: "destructive" });
    } finally { setDeleting(false); setDeleteId(null); }
  };

  const st = STATE[status?.state] || STATE.error;
  const Icon = st.icon;

  return (
    <div data-testid="metricool-manager">
      <h1 className="font-display font-extrabold text-2xl text-navy mb-1">Social Posting (Metricool)</h1>
      <p className="text-sm text-slate-500 mb-5">Push blog articles, podcast promos or custom posts to your Metricool planner as drafts to review, then publish from Metricool.</p>

      {status && (
        <div data-testid="metricool-status" className={`flex items-start gap-3 rounded-xl border px-4 py-3 mb-5 ${st.cls}`}>
          <Icon size={18} className="shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-sm">
            <p className="font-bold">Metricool: {status.configured ? (status.state === "ready" ? "Connected" : status.state === "auth_error" ? "Credentials rejected" : "Attention needed") : "Not set up"}</p>
            <p className="opacity-90 mt-0.5 leading-relaxed">{status.detail}</p>
          </div>
          <button data-testid="metricool-recheck" onClick={loadStatus} disabled={checking} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full bg-white/60 hover:bg-white disabled:opacity-50">
            <RefreshCw size={13} className={checking ? "animate-spin" : ""} /> Re-check
          </button>
        </div>
      )}

      <Button data-testid="metricool-new-post" onClick={() => setOpen(true)} disabled={status && !status.configured} className="bg-orange hover:bg-orange-dark text-white font-bold rounded-full px-5 mb-6">
        <Plus size={16} className="mr-1" /> New post
      </Button>

      <MetricoolComposer open={open} onClose={() => { setOpen(false); loadPosts(); }} initial={null} />

      <h2 className="font-display font-bold text-navy mb-3">Recently sent</h2>
      {posts.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-10 text-center text-slate-400"><Share2 size={32} className="mx-auto mb-3 text-slate-300" />Nothing sent to Metricool yet.</div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} data-testid={`metricool-post-${p.id}`} className="bg-white rounded-xl border border-slate-100 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-navy line-clamp-2">{p.text}</p>
                  <p className="text-xs text-slate-400 mt-2 flex items-center gap-2 flex-wrap">
                    {(p.networks || []).map((n) => { const NI = NET_ICON[n] || Share2; return <span key={n} className="inline-flex items-center gap-1"><NI size={12} /> {NET_LABEL[n] || n}</span>; })}
                    <span className="text-slate-300">·</span> {p.draft ? "Draft" : "Scheduled"} for {fmt(p.date_time)}
                    {p.source === "blog" && <><span className="text-slate-300">·</span> from a blog post</>}
                    {p.source === "podcast" && <><span className="text-slate-300">·</span> from a podcast</>}
                    {p.source === "assessment" && <><span className="text-slate-300">·</span> from an assessment</>}
                  </p>
                </div>
                <span className="text-xs text-slate-400 shrink-0">{fmt(p.created_at)}</span>
                <button data-testid={`metricool-delete-${p.id}`} onClick={() => setDeleteId(p.id)} title="Delete this post (removes it from Metricool too)" className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 shrink-0"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="metricool-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>This removes it from your Metricool planner too (if it hasn't already been published). This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-500 hover:bg-red-600 rounded-full">{deleting ? "Deleting…" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RecurringPromos configured={!status || status.configured} />
    </div>
  );
}
